window.Grape.Formulas = {
	run: function (formulaString, context) {
		const formula = this.createFormula(formulaString);
		return this.executeFormula(formula, context);
	},

	/**
	 * Creates a formula from a string definition
	 * @param {string} formulaString - The formula string (e.g., "SUM(10, MULTIPLY(2,2), $.val, 10)")
	 * @param {string} [formulaUuid] - Optional UUID for the formula
	 * @param {boolean} [isTopLevel=true] - Whether this is a top-level formula
	 * @returns {Object} The created formula object
	 */

	createFormula(formulaString, formulaUuid = null, isTopLevel = true) {
		// Regular expression to match function name and arguments
		const formulaRegex = /^([A-Za-z][A-Za-z0-9_-]*)\((.*)\)$/;
		const matches = formulaString.match(formulaRegex);

		if (!matches)
		{
			throw new Error(`Formula pattern is incorrect: ${formulaString}`);
		}

		const [, functionName, argumentString] = matches;

		// Get function handler
		const run_func = window.Grape.registry.getItem('FormulaFunctions', functionName?.toLowerCase())
		if (!run_func)
		{
			throw new Error(`Function ${functionName} not found`);
		}

		let uuid = formulaUuid;
		if (!uuid && crypto.randomUUID instanceof Function)
			uuid = crypto.randomUUID();
		if (!uuid)
		{
			uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
				const r = Math.random() * 16 | 0;
				const v = c === 'x' ? r : (r & 0x3 | 0x8);
				return v.toString(16);
			});
		}

		// Create formula object
		const formula = {
			formula_id: Date.now(), // Use timestamp as temporary ID
			formula_uuid: uuid,
			function_name: functionName,
			run_func: run_func,
			is_top_level: isTopLevel,
			definition: formulaString,
			parameters: []
		};

		// Parse and process arguments
		const args = this.parseArgumentString(argumentString);
		args.forEach((arg, idx) => {
			if (arg.type === 'formula')
			{
				// Recursively create nested formula
				const nestedFormula = this.createFormula(arg.value, null, false);
				formula.parameters.push({
					idx,
					type: arg.type,
					formula: nestedFormula
				});
			} else if (arg.type === 'context')
			{
				formula.parameters.push({
					idx,
					type: arg.type,
					context_selector: arg.value
				});
			} else
			{
				formula.parameters.push({
					idx,
					type: arg.type,
					parameter_value: arg.value
				});
			}
		});

		return formula;
	},

	parseArgumentString(argumentString) {
		const args = [];
		let currentArg = '';
		let depth = 0;
		let inString = false;
		let stringChar = '';

		for (let i = 0; i < argumentString.length; i++)
		{
			const char = argumentString[i];

			if (char === '"' || char === "'")
			{
				if (!inString)
				{
					inString = true;
					stringChar = char;
				} else if (char === stringChar)
				{
					inString = false;
				}
				currentArg += char;
			} else if (char === '(' && !inString)
			{
				depth++;
				currentArg += char;
			} else if (char === ')' && !inString)
			{
				depth--;
				currentArg += char;
			} else if (char === ',' && depth === 0 && !inString)
			{
				args.push(this.classifyArgument(currentArg.trim()));
				currentArg = '';
			} else
			{
				currentArg += char;
			}
		}

		if (currentArg.trim())
		{
			args.push(this.classifyArgument(currentArg.trim()));
		}

		return args;
	},

	classifyArgument(arg) {
		const formulaRegex = /^([A-Za-z][A-Za-z0-9_-]*)\((.*)\)$/;
		const contextRegex = /^[cC][tT][xX]\("(.*)"\)$/;

		// Check if it's ctx
		if (contextRegex.test(arg))
		{

			const [, ctx] = arg.match(contextRegex);
			return {
				type: 'context',
				value: ctx
			}
		}

		// Check if it's formula
		if (arg.match(formulaRegex))
		{
			return {
				type: 'formula',
				value: arg
			};
		}

		// Try to parse as JSON
		try
		{
			const parsed = JSON.parse(arg);
			return {
				type: 'value',
				value: parsed
			};
		} catch (e)
		{
			// If not valid JSON, treat as string
			return {
				type: 'value',
				value: arg
			};
		}
	},

	/**
	 * Evaluates a context selector against a context object
	 * @param {Object} context - The context object
	 * @param {string} selector - The context selector path
	 * @returns {*} The selected value from the context
	 */
	evaluateContextSelector: function (context, selector) {
		try
		{
			const path = selector.replace(/^\$\./, '').split('.');
			let result = context;
			for (const key of path)
			{
				result = result[key];
				if (result === undefined) return null;
			}
			return result;
		} catch (error)
		{
			return null;
		}
	},

	/**
	 * Executes a formula with the given context
	 * @param {Object} formula - The formula object containing function_name and parameters
	 * @param {Object} context - The context object containing values for context selectors
	 * @param {Object} options - Additional options
	 * @param {boolean} options.enableCache - Whether to enable caching of results
	 * @param {Object} options.cache - Cache object to store results
	 * @returns {*} The result of the formula execution
	 */
	executeFormula: function (formula, context, options = {}) {
		const { enableCache = false, cache = new Map() } = options;

		// Check if we have a cached result
		// if (enableCache)
		// {
		// 	const cacheKey = JSON.stringify({ formulaId: formula.formula_id, context });
		// 	if (cache.has(cacheKey))
		// 	{
		// 		return cache.get(cacheKey);
		// 	}
		// }

		// Collect parameters
		const params = [];

		// Process each parameter
		for (const param of formula.parameters || [])
		{
			let result;

			if (param.type === 'context')
			{
				// Handle context selector (similar to jsonb_path_query)
				result = this.evaluateContextSelector(context, param.context_selector);
			} else if (param.type === 'value')
			{
				// Use direct parameter value
				result = param.parameter_value;
			} else if (param.type === 'formula')
			{
				// Recursively execute nested formula
				result = this.executeFormula(
					param.formula,
					context,
					options
				);
			} else
			{
				throw new Error('The parameter to be executed could not be identified');
			}

			// Handle array/null cases similar to SQL implementation
			if (Array.isArray(result) || result === null)
			{
				params.push([result]);
			} else
			{
				params.push(result);
			}
		}

		// Execute the function
		const result = formula.run_func(...params);
		// const result = window.Grape.registry.get('FormulaFunctions', formula.function_id?.toLowerCase())(...params);

		// Cache the result if enabled
		// if (enableCache)
		// {
		// 	const cacheKey = JSON.stringify({ formulaId: formula.formula_id, context });
		// 	cache.set(cacheKey, result);
		// }

		return result;
	}
};
